/** @format */

import {
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { Alert, Button, DatePicker, Input, Pagination, Segmented, Select, Spin } from "antd";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import styled from "styled-components";
import {
  apiIntermittentList,
  apiIntermittentSummary,
  intermittentErrorMessage,
} from "../../../api/apiIntermittentCommunications";
import {
  TIntermittentFilters,
  TIntermittentList,
  TIntermittentStatus,
  TIntermittentSummary,
  TIntermittentWorkerType,
} from "../../../redux-modules/declarations/maps/intermittentCommunications";
import { C } from "../../../theme/maps/StatiPresenze";
import UITransition from "../../ui/Transition";
import { STATUS_META, STATUS_ORDER } from "./constants";
import { plurale } from "./format";
import { DettaglioComunicazione } from "./partials/DettaglioComunicazione";
import { ElencoComunicazioni } from "./partials/ElencoComunicazioni";
import { BarraStrumenti, Intestazione, Pagina, Piedino, Regole, Schede, Stato } from "./styles";

/** Evento per aggiornare il numero nel menu laterale dopo un'azione. */
export const INTERMITTENT_REFRESH_EVENT = "intermittent-communications:refresh";

const PER_PAGINA = 20;

type TVista = "da-gestire" | "in-programma" | "inviate" | "annullamento" | "tutte";

const VISTE: TVista[] = ["da-gestire", "in-programma", "inviate", "annullamento", "tutte"];

interface TDatiVista {
  scheda: string;
  /** stati richiesti al backend; "da-gestire" usa needs_action, "tutte" il filtro della barra */
  stati?: TIntermittentStatus[];
  /** spiegazione sotto la barra: cosa c'è (e cosa non c'è) in elenco */
  spiegazione: string;
  vuoto: string;
}

const DATI_VISTA: Record<TVista, TDatiVista> = {
  "da-gestire": {
    scheda: "Da gestire",
    spiegazione:
      "Comunicazioni che richiedono un intervento. Quelle in programma, inviate o chiuse sono nelle altre schede.",
    vuoto: "Nessuna comunicazione da gestire.",
  },
  "in-programma": {
    scheda: "In programma",
    stati: ["scheduled"],
    spiegazione: "Partiranno in automatico all'orario indicato.",
    vuoto: "Nessuna comunicazione in programma.",
  },
  inviate: {
    scheda: "Inviate",
    stati: ["sent"],
    spiegazione: "In attesa del controllo presenze dopo il turno.",
    vuoto: "Nessuna comunicazione inviata in attesa di controllo.",
  },
  annullamento: {
    scheda: "In attesa di annullamento",
    stati: ["cancellation_pending"],
    spiegazione:
      "Nessuna timbratura valida: se il lavoratore era presente, intervieni prima dell'annullamento automatico.",
    vuoto: "Nessuna comunicazione in attesa di annullamento.",
  },
  tutte: {
    scheda: "Tutte",
    spiegazione: "Tutte le comunicazioni, in qualsiasi stato.",
    vuoto: "Nessuna comunicazione.",
  },
};

/** Stato della pagina nell'indirizzo, come le Presenze: si può tornare indietro e condividere il link. */
interface TIndirizzo {
  vista: TVista | null;
  id: number | null;
}

const leggiIndirizzo = (search: string): TIndirizzo => {
  const q = new URLSearchParams(search);
  const vista = q.get("vista") as TVista;
  const id = q.get("id");
  return {
    vista: VISTE.indexOf(vista) >= 0 ? vista : null,
    id: id && /^\d+$/.test(id) ? Number(id) : null,
  };
};

const scriviIndirizzo = (i: TIndirizzo): string => {
  const q = new URLSearchParams();
  if (i.vista) q.set("vista", i.vista);
  if (i.id) q.set("id", String(i.id));
  const testo = q.toString();
  return testo ? `?${testo}` : "";
};

/**
 * Comunicazioni obbligatorie di intermittenti e occasionali (solo ROOT e AMMINISTRATORI).
 * Stesse API e stessa logica della produzione, con la grafica di Home, Turni e Presenze:
 * schede al posto dei contatori, elenco per giorno e pannello laterale con le azioni in linea.
 */
export const ViewIntermittentCommunications = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const indirizzo = useMemo(() => leggiIndirizzo(location.search), [location.search]);

  const [riepilogo, setRiepilogo] = useState<TIntermittentSummary | null>(null);
  const [elenco, setElenco] = useState<TIntermittentList | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [lettura, setLettura] = useState(0);

  const [testoRicerca, setTestoRicerca] = useState("");
  const [ricerca, setRicerca] = useState("");
  const [periodo, setPeriodo] = useState<[Moment, Moment] | null>(null);
  const [tipologia, setTipologia] = useState<TIntermittentWorkerType | "tutte">("tutte");
  const [stati, setStati] = useState<TIntermittentStatus[]>([]);
  const [ordine, setOrdine] = useState<"asc" | "desc">("desc");
  const [pagina, setPagina] = useState(1);
  const richiesta = useRef(0);

  // senza una scheda nell'indirizzo si parte da "Da gestire" se c'è qualcosa, altrimenti da "Tutte"
  const vista: TVista | null =
    indirizzo.vista || (riepilogo ? (riepilogo.needs_action > 0 ? "da-gestire" : "tutte") : null);

  const vai = useCallback(
    (modifica: Partial<TIndirizzo>, aggiungi = false): void => {
      const { pathname, search } = history.location;
      const attuale = leggiIndirizzo(search);
      const nuovo = `${pathname}${scriviIndirizzo({
        ...attuale,
        vista: attuale.vista || vista,
        ...modifica,
      })}`;
      if (aggiungi) history.push(nuovo);
      else history.replace(nuovo);
    },
    [history, vista],
  );

  const leggiRiepilogo = useCallback((): void => {
    apiIntermittentSummary()
      .then(setRiepilogo)
      .catch(err => {
        setErrore(intermittentErrorMessage(err, "Impossibile caricare il riepilogo."));
        // senza riepilogo si mostra comunque l'elenco completo
        setRiepilogo(
          r => r || { by_status: [], needs_action: 0, enabled: true, send_before_hours: 25, xml_recipients: [] },
        );
      });
  }, []);

  useEffect(() => {
    leggiRiepilogo();
  }, [leggiRiepilogo]);

  // la ricerca parte dopo una breve pausa nella scrittura
  useEffect(() => {
    const t = window.setTimeout(() => {
      setRicerca(testoRicerca.trim());
      setPagina(1);
    }, 350);
    return (): void => window.clearTimeout(t);
  }, [testoRicerca]);

  useEffect(() => {
    if (!vista) return;
    const filtri: TIntermittentFilters = {
      page: pagina,
      per_page: PER_PAGINA,
      sort: ordine,
      search: ricerca || undefined,
      worker_type: tipologia === "tutte" ? undefined : tipologia,
      date_from: periodo ? periodo[0].format("YYYY-MM-DD") : undefined,
      date_to: periodo ? periodo[1].format("YYYY-MM-DD") : undefined,
    };
    if (vista === "da-gestire") filtri.needs_action = true;
    else if (vista === "tutte") filtri.status = stati.length ? stati : undefined;
    else filtri.status = DATI_VISTA[vista].stati;

    const numero = ++richiesta.current;
    setCaricamento(true);
    apiIntermittentList(filtri)
      .then(risposta => {
        if (numero !== richiesta.current) return;
        setElenco(risposta);
        setErrore(null);
      })
      .catch(err => {
        if (numero !== richiesta.current) return;
        setErrore(intermittentErrorMessage(err, "Impossibile caricare le comunicazioni."));
      })
      .finally(() => {
        if (numero === richiesta.current) setCaricamento(false);
      });
  }, [vista, pagina, ordine, ricerca, tipologia, periodo, stati, lettura]);

  const aggiornaTutto = useCallback((): void => {
    leggiRiepilogo();
    setLettura(n => n + 1);
    window.dispatchEvent(new Event(INTERMITTENT_REFRESH_EVENT));
  }, [leggiRiepilogo]);

  const scegliVista = (nuova: TVista): void => {
    setPagina(1);
    setStati([]);
    vai({ vista: nuova, id: null }, true);
  };

  const azzeraFiltri = (): void => {
    setTestoRicerca("");
    setRicerca("");
    setPeriodo(null);
    setTipologia("tutte");
    setStati([]);
    setPagina(1);
  };

  const conFiltri =
    !!ricerca || !!periodo || tipologia !== "tutte" || (vista === "tutte" && stati.length > 0);

  const conteggio = (v: TVista): number | null => {
    if (!riepilogo || v === "tutte") return null;
    if (v === "da-gestire") return riepilogo.needs_action;
    const stato = DATI_VISTA[v].stati?.[0];
    return riepilogo.by_status.find(s => s.status === stato)?.total ?? 0;
  };

  const oggi = moment().format("YYYY-MM-DD");
  const totale = elenco?.pagination.total ?? 0;
  const datiVista = vista ? DATI_VISTA[vista] : null;

  return (
    <UITransition>
      <Pagina>
        <Intestazione>
          <div>
            <h1 data-cy="page-title">Comunicazioni intermittenti</h1>
            <Regole>
              <span>
                Chiamate di intermittenti e occasionali:{" "}
                <strong>
                  invio automatico {riepilogo?.send_before_hours ?? 25} ore prima del turno
                </strong>
                , annullamento se manca la timbratura.
              </span>
              {riepilogo && (
                <span data-cy="intermittent-xml-recipients">
                  <MailOutlined />
                  XML a:{" "}
                  {riepilogo.xml_recipients.length ? (
                    <strong>{riepilogo.xml_recipients.join(", ")}</strong>
                  ) : (
                    <span className="mancante">destinatario non configurato</span>
                  )}
                </span>
              )}
            </Regole>
          </div>
          <div className="azioni">
            <Button icon={<ReloadOutlined />} onClick={aggiornaTutto} loading={caricamento}>
              Aggiorna
            </Button>
          </div>
        </Intestazione>

        {riepilogo && !riepilogo.enabled && (
          <Alert
            type="warning"
            showIcon
            message="Invio automatico disattivato"
            description="Le comunicazioni non vengono pianificate né inviate in automatico. Le azioni manuali restano disponibili."
          />
        )}

        <SchedeComunicazioni aria-label="Comunicazioni per stato">
          {VISTE.map(v => {
            const n = conteggio(v);
            return (
              <button
                key={v}
                type="button"
                aria-current={vista === v ? "page" : undefined}
                onClick={(): void => scegliVista(v)}
                data-cy={`intermittent-card-${v}`}
              >
                {DATI_VISTA[v].scheda}
                {n !== null && (n > 0 || v === "da-gestire") && (
                  <span
                    className={`conteggio${
                      v === "da-gestire" && n > 0 ? "" : v === "annullamento" ? " ambra" : " neutro"
                    }`}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </SchedeComunicazioni>

        <BarraStrumenti>
          <div className="riga">
            <Input
              className="ricerca"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cerca dipendente o codice fiscale"
              aria-label="Cerca dipendente o codice fiscale"
              value={testoRicerca}
              onChange={(e): void => setTestoRicerca(e.target.value)}
            />
            <DatePicker.RangePicker
              format="DD/MM/YYYY"
              placeholder={["Dal giorno", "Al giorno"]}
              aria-label="Giorno del turno"
              value={periodo}
              onChange={(v): void => {
                setPeriodo(v && v[0] && v[1] ? [v[0], v[1]] : null);
                setPagina(1);
              }}
              ranges={{
                Oggi: [moment(), moment()],
                Domani: [moment().add(1, "day"), moment().add(1, "day")],
                "Prossimi 7 giorni": [moment(), moment().add(7, "days")],
                "Ultimi 7 giorni": [moment().subtract(7, "days"), moment()],
              }}
            />
            <Segmented
              aria-label="Tipologia"
              value={tipologia}
              onChange={(v): void => {
                setTipologia(v as TIntermittentWorkerType | "tutte");
                setPagina(1);
              }}
              options={[
                { label: "Tutti", value: "tutte" },
                { label: "Intermittenti", value: "intermittente" },
                { label: "Occasionali", value: "occasionale" },
              ]}
            />
            {vista === "tutte" && (
              <Select
                className="filtro"
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder="Tutti gli stati"
                aria-label="Stati"
                value={stati}
                optionFilterProp="label"
                options={STATUS_ORDER.map(s => ({ value: s, label: STATUS_META[s].label }))}
                onChange={(v: TIntermittentStatus[]): void => {
                  setStati(v);
                  setPagina(1);
                }}
              />
            )}
          </div>
          <div className="riga">
            {datiVista && <Stato>{datiVista.spiegazione}</Stato>}
            <div className="spazio" />
            {caricamento && elenco && <Spin size="small" />}
            <Button
              size="small"
              type="text"
              icon={ordine === "desc" ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={(): void => {
                setOrdine(o => (o === "desc" ? "asc" : "desc"));
                setPagina(1);
              }}
              title="Cambia l'ordine per giorno del turno"
            >
              {ordine === "desc" ? "Data del turno: decrescente" : "Data del turno: crescente"}
            </Button>
            {conFiltri && (
              <Button size="small" type="link" onClick={azzeraFiltri}>
                Azzera filtri
              </Button>
            )}
          </div>
        </BarraStrumenti>

        {errore && (
          <Alert
            type="error"
            showIcon
            message={errore}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={aggiornaTutto}>
                Riprova
              </Button>
            }
          />
        )}

        <Spin spinning={caricamento && !elenco}>
          <ElencoComunicazioni
            comunicazioni={elenco?.data || []}
            oggi={oggi}
            aperta={indirizzo.id}
            vuoto={{
              titolo: !elenco
                ? "Caricamento delle comunicazioni…"
                : conFiltri
                ? "Nessuna comunicazione con questi filtri."
                : datiVista?.vuoto || "Nessuna comunicazione.",
              suggerimento: conFiltri
                ? "Cambia periodo o tipologia, oppure svuota la ricerca."
                : "Le comunicazioni si creano da sole quando un intermittente o un occasionale ha un turno.",
            }}
            onApri={(id): void => vai({ id }, true)}
          />
        </Spin>

        {totale > 0 && (
          <Piedino>
            <span>{plurale(totale, "comunicazione", "comunicazioni")}</span>
            {totale > PER_PAGINA && (
              <Pagination
                size="small"
                current={elenco?.pagination.current || pagina}
                pageSize={PER_PAGINA}
                total={totale}
                showSizeChanger={false}
                onChange={(p): void => {
                  setPagina(p);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </Piedino>
        )}
      </Pagina>

      <DettaglioComunicazione
        id={indirizzo.id}
        destinatariXml={riepilogo?.xml_recipients}
        onChiudi={(): void => vai({ id: null })}
        onModificata={aggiornaTutto}
      />
    </UITransition>
  );
};

/** Le schede delle Presenze hanno il numero in rosso: qui solo "Da gestire" lo è. */
const SchedeComunicazioni = styled(Schede)`
  .conteggio.neutro {
    background: #e3e5ef;
    color: #3a4160;
  }
  .conteggio.ambra {
    background: #fdebc8;
    color: ${C.avviso.testo};
  }
`;
